import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { api, asArray, whatsappLink } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageBits";
import { useQuote } from "@/context/QuoteContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Products() {
  useSEO({ title: "Products", description: "Explore Leaf Alchemy's premium tea powders — hotel grade, premium, elaichi, masala and chocolate tea powder for bulk supply across India." });
  const { openQuote } = useQuote();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products")
      .then((r) => setProducts(asArray(r.data)))
      .catch(() => setProducts([]));
  }, []);

  const current = products.filter((p) => !p.future);
  const future = products.filter((p) => p.future);

  return (
    <div data-testid="products-page">
      <PageHero eyebrow="Our Collection" title="Premium Tea Powders for Every Palate"
        subtitle="Blended for strength, aroma and consistency — available in retail and bulk pack sizes." />

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {current.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <div className="group bg-cream rounded-2xl overflow-hidden border border-forest/10 hover:border-gold/40 transition-colors shadow-[0_8px_32px_rgba(15,81,50,0.06)] hover:shadow-[0_16px_48px_rgba(200,164,77,0.18)] h-full flex flex-col" data-testid={`product-card-${p.slug}`}>
                  <Link to={`/products/${p.slug}`} className="overflow-hidden h-60 rounded-b-[3rem] block">
                    <img src={p.images?.[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </Link>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-[11px] uppercase tracking-widest text-olive">{p.category}</span>
                    <h3 className="font-display text-2xl text-forest mt-1">{p.name}</h3>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.description}</p>
                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {(p.pack_sizes || []).slice(0, 4).map((s) => (
                        <Badge key={s} variant="outline" className="border-forest/20 text-forest/70 text-[11px]">{s}</Badge>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-auto pt-5">
                      <Button onClick={() => openQuote({ product: p.name })} data-testid={`product-quote-${p.slug}`} className="rounded-full bg-forest hover:bg-olive text-cream flex-1 transition-colors">Request Quote</Button>
                      <a href={whatsappLink(`Hi, I'm interested in ${p.name}.`)} target="_blank" rel="noreferrer" className="w-11 h-11 shrink-0 grid place-items-center rounded-full bg-[#25D366] text-white" data-testid={`product-wa-${p.slug}`}>
                        <MessageCircle size={18} />
                      </a>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {future.length > 0 && (
            <div className="mt-24">
              <h2 className="font-display text-3xl text-forest text-center mb-3">Coming Soon</h2>
              <p className="text-center text-muted-foreground mb-10">New blends joining the Leaf Alchemy range.</p>
              <div className="grid gap-6 sm:grid-cols-3 max-w-4xl mx-auto">
                {future.map((p, i) => (
                  <Reveal key={p.id} delay={i * 0.06}>
                    <div className="relative bg-cream/60 border border-forest/10 rounded-2xl overflow-hidden" data-testid={`future-product-${p.slug}`}>
                      <div className="h-40 overflow-hidden">
                        <img src={p.images?.[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover opacity-70" />
                      </div>
                      <div className="p-5 text-center">
                        <Badge className="bg-gold text-ink mb-2">Coming Soon</Badge>
                        <h3 className="font-display text-xl text-forest">{p.name}</h3>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
